import {
  ControlCoreBase,
  IDashboardPage,
  IDashboardParametersBase,
  IExternalPlugin,
  IItem,
  IPluginConfig,
  IPluginFieldHandler,
  IPluginFieldValueBase,
  IPluginSettingPage,
  ITool,
  PluginCore,
  registerPlugin,
  IServerSettingsBase,
  IProjectSettingsBase,
  Project,
} from 'matrix-requirements-sdk/client';
import { sdkInstance } from './Instance';
import { IDashboardParameters, IProjectSettings } from './Interfaces';
import { EmptyPluginFieldHandler } from './helpers/EmptyPluginFieldHandler';
import { DashboardPage } from './DashboardPage';
import { Tool } from './Tools';
import { Tile } from './Tile';

/** This class is allows you to configure the features of your plugin.
 *
 *  You can also implement functions to into the plugin (at start in the constructor, when loading a project, when loading an item)
 *
 */
export class Plugin
  implements
    IExternalPlugin<
      IServerSettingsBase,
      IProjectSettings,
      EmptyPluginFieldHandler,
      IPluginFieldValueBase,
      IDashboardParameters
    >
{
  core: PluginCore;

  /**This part enables which
   *
   * See IPluginConfig interface for explanation of parameters
   */

  static config: IPluginConfig<IServerSettingsBase, IProjectSettings> = {
    /*  Page in admin client to configure settings across all projects - set enabled to false if not needed.
        The page itself is implemented in the _ServerSettingsPage.ts
    */
    customerSettingsPage: {
      id: 'TRACEcustomerSettings',
      title: 'Trace customer settings page',
      type: 'TRACEcs',
      enabled: false,
      defaultSettings: {},
      settingName: 'TraceConfig',
      help: '',
      helpUrl: 'https://urlshort.matrixreq.com/d24/',
    },
    /*  Page in admin client to configure settings for one specific project - set enabled to false if not needed.
        The page itself is implemented in the _ProjectSetingsPage.ts
    */
    projectSettingsPage: {
      id: 'teoConfig',
      title: 'Test Dashboard Configuration',
      type: 'teoConfig',
      enabled: false,
      defaultSettings: {},
      settingName: 'teo_config',
      help: '',
      helpUrl: undefined,
    },
    /*  Add an entry in the tool menu of an item or folder - set enabled to false if not needed.
        The tool itself is implemented in the _Tool.ts
    */
    menuToolItem: {
      enabled: true,
      title: 'Traceability Overview',
    },
    /*  Add a custom field to enter some data in the UI - set enabled to false if not needed.
        The field itself is implemented in the _Control.ts
    */
    field: {
      enabled: false,
      fieldType: 'teo',
      title: 'Trace',
      fieldConfigOptions: {
        id: 'teo',
        capabilities: {
          canBePublished: false,
          canBeReadonly: true,
          canBeXtcPreset: false,
          canHideInDoc: false,
          canBeUsedInDocs: false,
          canRequireContent: false,
        },
        class: '',
        help: '',
        label: 'Trace',
      },
    },
    /*  Add a dashboard inside a project - set enabled to false if not needed.
        The field itself is implemented in the _Control.ts
    */
    dashboard: {
      id: 'TRACE',
      title: 'Traceability Overview',
      enabled: true,
      icon: 'fal fa-sitemap',
      parent: 'DASHBOARDS',
      usefilter: true,
      order: 9999,
    },
  };
  private currentProject: Project;
  /**
   * The constructor is loaded once after all the source code is loaded by the browser.
   * Nothing is known about the instance, project, user etc.
   * You can use it to initialize things like callbacks after item changes
   */
  constructor() {
    // here is a good place to register callbacks for UI events (like displaying or saving items)
    this.core = new sdkInstance.PluginCore(this);
  }

  PLUGIN_VERSION = require('../resources/matrix.json').version;
  PLUGIN_NAME = 'TRACE';

  private async setupProject(newProjectName?: string) {
    if (newProjectName === undefined) {
      // Get the current project from the session.
      newProjectName = sdkInstance.matrixSession.getProject();
    }
    if (this.currentProject) {
      // Did we change projects?
      if (newProjectName && this.currentProject.getName() != newProjectName) {
        this.currentProject = null;
      }
    }
    if (this.currentProject == null) {
      this.currentProject = newProjectName
        ? await sdkInstance.matrixsdk.openProject(newProjectName)
        : await sdkInstance.matrixsdk.openCurrentProjectFromSession();
    }
  }

  async getDashboardAsync(): Promise<IDashboardPage<IDashboardParameters>> {
    // Whoa, now is my chance to load from the web
    // I can do "slow" things here if necessary.
    await this.setupProject();
    // TODO: projectStorage should be available on Project.
    return new DashboardPage(
      this.currentProject,
      sdkInstance.globalMatrix.projectStorage,
    );
  }
  async getProjectSettingsPageAsync(): Promise<
    IPluginSettingPage<IProjectSettingsBase>
  > {
    return null;
  }
  async getServerSettingsPageAsync(): Promise<
    IPluginSettingPage<IServerSettingsBase>
  > {
    await this.setupProject();

    return null;
  }
  async getControlAsync(
    ctrlObj: JQuery,
  ): Promise<ControlCoreBase<EmptyPluginFieldHandler, IPluginFieldValueBase>> {
    await this.setupProject();
    return null;
  }
  async getToolAsync(): Promise<ITool> {
    await this.setupProject();
    return new Tool();
  }
  getConfig() {
    return Plugin.config;
  }
  enableToolMenu(ul: JQuery, _hook: number): boolean {
    return this.core.enabledInContext;
  }
  /**
   * This method is called each time  a project has been loaded and initialized.
   * At the time it is called, all project settings, categories etc are defined.
   *
   * @param project // id of the loaded project
   */
  onInitProject(project: string) {
    // here is a good place to decide based on the project (maybe some project setting), whether the plugin should be enabled

    // if not:
    // this.enabledInContext = false;
    this.setupProject(project);
  }

  /** this method is called just before the rendering of an item is done
   * It is also called when opening the create item dialog.
   *
   * @param _item: the item which is being loaded in the UI
   */
  onInitItem(item: IItem) {
    // here is a good place to decide based on the selection in the tree, whether the plugin should be enabled
    // if not:
    // this.enabledInContext = false;
  }
  getTiles() {
    return [new Tile()];
  }
  async getDashboardLinksForAnalyticsAsync() {
    if (await this.core.enableDashboardAsync()) {
      return [
        {
          pageId: Plugin.config.dashboard.id,
          title: Plugin.config.dashboard.title,
        },
      ];
    }
    return [];
  }
}

registerPlugin(new Plugin().core);
